import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import type { ReactNode } from 'react';
import type { PaginatedResponse } from '../../../types';
import TrashIcon from './icons/TrashIcon';

type ItemWithId = { id: string };

type SortedProps<T extends ItemWithId> = {
  route: string;
  render: (
    item: T,
    index: number,
    items: T[],
    setItems: React.Dispatch<React.SetStateAction<T[]>>
  ) => ReactNode;
  emptyMessage?: string;
  transform?: (items: T[]) => T[];
  deps?: unknown[];
  disabled?: boolean;
  limit?: number;
  onReorder?: (items: T[]) => Promise<void> | void;
  onRemove?: (item: T) => Promise<void> | void;
  getId?: (item: T) => string;
};

function SortableItem<T extends ItemWithId>({
  item,
  index,
  items,
  setItems,
  render,
  onRemove,
  getId,
}: {
  item: T;
  index: number;
  items: T[];
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
  render: (
    item: T,
    index: number,
    items: T[],
    setItems: React.Dispatch<React.SetStateAction<T[]>>
  ) => ReactNode;
  onRemove?: (item: T) => Promise<void> | void;
  getId: (item: T) => string;
}) {
  const id = getId(item);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  } as const;

  const handleDelete = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setItems(items.filter((i) => getId(i) !== id));
    await onRemove?.(item);
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          margin: '0 10px',
          gap: '10px',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {render(item, index, items, setItems)}
        </div>
        <button
          onClick={handleDelete}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            margin: '-8px',
            lineHeight: 0,
            touchAction: 'manipulation',
          }}
        >
          <TrashIcon size={30} />
        </button>
        <button
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'grab',
            padding: '8px',
            margin: '-8px',
            lineHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="var(--primary-soft)"
          >
            <path d="M5 7h14v2H5V7zm0 4h14v2H5v-2zm0 4h14v2H5v-2z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function Sorted<T extends ItemWithId>({
  route,
  render,
  emptyMessage = 'No items',
  transform,
  deps = [],
  disabled = false,
  limit = 50,
  onReorder,
  onRemove,
  getId = (item: T) => item.id,
}: SortedProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    })
  );

  const fetchAllPages = useCallback(async () => {
    setLoading(true);
    try {
      let page = 1;
      let hasMore = true;
      const collected: T[] = [];

      while (hasMore) {
        const url = `${route}${route.includes('?') ? '&' : '?'}page=${page}&limit=${limit}`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to fetch');
        const data: PaginatedResponse<T> = await res.json();
        collected.push(...data.items);
        hasMore = data.hasMore;
        page += 1;
      }

      const newItems = transform ? transform(collected) : collected;
      setItems(newItems);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, [limit, route, transform]);

  useEffect(() => {
    if (disabled) return;
    fetchAllPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, ...deps, fetchAllPages]);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setItems((prevItems) => {
        const oldIndex = prevItems.findIndex(
          (item) => getId(item) === active.id
        );
        const newIndex = prevItems.findIndex((item) => getId(item) === over.id);
        if (oldIndex === -1 || newIndex === -1) return prevItems;
        const reordered = arrayMove(prevItems, oldIndex, newIndex);
        void onReorder?.(reordered);
        return reordered;
      });
    },
    [getId, onReorder]
  );

  const renderedItems = useMemo(
    () =>
      items.map((item, index) => (
        <SortableItem
          key={getId(item)}
          item={item}
          index={index}
          items={items}
          setItems={setItems}
          render={render}
          onRemove={onRemove}
          getId={getId}
        />
      )),
    [getId, items, onRemove, render]
  );

  if (items.length === 0 && !loading) {
    return (
      <p className="subtle-text" style={{ textAlign: 'center' }}>
        {emptyMessage}
      </p>
    );
  }

  return (
    <div style={{ overflowX: 'hidden' }}>
      <DndContext
        collisionDetection={closestCenter}
        sensors={sensors}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => getId(item))}
          strategy={verticalListSortingStrategy}
        >
          {renderedItems}
        </SortableContext>
        {loading && (
          <p
            className="subtle-text"
            style={{ textAlign: 'center', padding: '10px' }}
          >
            Loading...
          </p>
        )}
      </DndContext>
    </div>
  );
}

export default Sorted;
